import type { OpenAPI } from "simplydo/interfaces";
import type { MessageThreadWithParticipants } from "simplydo/interfaces/types/messageTypes";

import type { NewMessageThread } from "components/lib/Messaging/types";

type MessageThread = OpenAPI.Schemas["MessageThread"];

export type SelectedMessageThread =
  | MessageThreadWithParticipants
  | MessageThread
  | NewMessageThread
  | string
  | { _id: string; participantUsers?: OpenAPI.Schemas["User"][] };

export const selectedThreadId = (thread: SelectedMessageThread): string =>
  typeof thread === "string" ? thread : thread._id;

export type MessagesReducer = {
  messageThreads: MessageThread[];
  newMessageCount: number;
  selectedMessageThreads: SelectedMessageThread[];
};

export type MessagesAction =
  | {
      type: "MESSAGE_THREADS_RECEIVED";
      threads: MessageThread[];
      newMessageCount: number;
    }
  | {
      type: "MESSAGE_THREAD_SELECTED";
      thread: SelectedMessageThread | null;
      isPhone: boolean;
    }
  | { type: "MESSAGE_THREADS_EXTEND"; threads: MessageThread[] }
  | {
      type: "MARK_MESSAGE_THREADS_AS_SEEN";
      messageThreads: string[];
      markAllAsSeen: boolean;
      userId: string;
      newMessageCount: number;
    }
  | { type: "CLOSE_MESSAGE_THREAD"; threadId: string }
  | { type: "LOGOUT" }
  | { type: "ON_THREAD_CREATED"; newThreadId: string; thread: MessageThread };

const initialState: MessagesReducer = {
  messageThreads: [],
  newMessageCount: 0,
  selectedMessageThreads: [],
};

function messages(
  state = initialState,
  action: MessagesAction,
): MessagesReducer {
  switch (action.type) {
    case "MESSAGE_THREADS_RECEIVED":
      return {
        ...state,
        messageThreads: action.threads,
        newMessageCount: action.newMessageCount,
      };
    case "MESSAGE_THREAD_SELECTED": {
      if (action.thread === null) {
        return { ...state, selectedMessageThreads: [] };
      }
      if (action.isPhone) {
        const thread: SelectedMessageThread =
          typeof action.thread === "string"
            ? { _id: action.thread }
            : action.thread;
        return { ...state, selectedMessageThreads: [thread] };
      }
      const thread =
        typeof action.thread === "string"
          ? state.messageThreads.find((t) => t._id === action.thread) ||
            state.selectedMessageThreads.find(
              (t) => selectedThreadId(t) === action.thread,
            )
          : "_id" in action.thread && !("createdAt" in action.thread)
            ? (action.thread as MessageThread)
            : (action.thread as MessageThread);
      const { selectedMessageThreads } = state;
      if (!thread) return state;
      const resolvedThread = thread as MessageThread;
      const threadIndex = selectedMessageThreads.findIndex(
        (t) => selectedThreadId(t) === selectedThreadId(resolvedThread),
      );
      if (threadIndex > -1) {
        const updatedThreads = selectedMessageThreads.map((t, index) => {
          if (index !== threadIndex) return t;
          return typeof t === "string"
            ? resolvedThread
            : { ...t, ...resolvedThread };
        });
        return { ...state, selectedMessageThreads: updatedThreads };
      }
      if (selectedMessageThreads.length > 2) {
        const slicedMessageThreads = selectedMessageThreads.slice(0, 2);
        return {
          ...state,
          selectedMessageThreads: [resolvedThread, ...slicedMessageThreads],
        };
      }
      return {
        ...state,
        selectedMessageThreads: [resolvedThread, ...selectedMessageThreads],
      };
    }
    case "CLOSE_MESSAGE_THREAD": {
      const updatedThreads = state.selectedMessageThreads.filter(
        (t) => selectedThreadId(t) !== action.threadId,
      );
      return { ...state, selectedMessageThreads: updatedThreads };
    }
    case "MESSAGE_THREADS_EXTEND": {
      const { messageThreads } = state;
      const threadsToAdd: MessageThread[] = [];
      action.threads.forEach((thread: MessageThreadWithParticipants) => {
        const existingThread = messageThreads.find((t) => t._id === thread._id);
        if (!existingThread) threadsToAdd.push(thread);
      });
      const newMessageThreads = [...messageThreads, ...threadsToAdd];
      newMessageThreads.sort((a, b) =>
        (a.lastMessageAt ?? 0) > (b.lastMessageAt ?? 0) ? 1 : -1,
      );
      return { ...state, messageThreads: newMessageThreads };
    }
    case "MARK_MESSAGE_THREADS_AS_SEEN": {
      const { messageThreads } = state;
      const {
        messageThreads: messageThreadsSeen,
        markAllAsSeen,
        userId,
      } = action;
      const updatedMessageThreads = messageThreads.map((n) => {
        if (markAllAsSeen || messageThreadsSeen.indexOf(n._id) > -1)
          return { ...n, seenBy: [...(n.seenBy ?? []), userId] };
        return n;
      });
      return {
        ...state,
        messageThreads: updatedMessageThreads,
        newMessageCount: 0,
      };
    }
    case "LOGOUT": {
      return { ...state, messageThreads: [], selectedMessageThreads: [] };
    }
    case "ON_THREAD_CREATED": {
      const { messageThreads, selectedMessageThreads } = state;
      const existingNewThread = messageThreads.findIndex(
        (t) => t._id === action.newThreadId,
      );
      const existingSelectedThread = selectedMessageThreads.findIndex(
        (t) => selectedThreadId(t) === action.newThreadId,
      );
      const updatedThreads = [...messageThreads];
      const updatedSelectedThreads = [...selectedMessageThreads];
      if (existingNewThread !== -1) {
        updatedThreads[existingNewThread] = action.thread;
      } else updatedThreads.push(action.thread);
      if (existingSelectedThread !== -1) {
        updatedSelectedThreads[existingSelectedThread] = action.thread;
      }
      return {
        ...state,
        messageThreads: updatedThreads,
        selectedMessageThreads: updatedSelectedThreads,
      };
    }
    default:
      return state;
  }
}

export default messages;
